import {Component, OnInit}             from '@angular/core';
import {combineLatest, Observable, of} from 'rxjs';
import {ApDynGridColumnConfigBase}     from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynGridStatusColumnConfig}   from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {ApDynGridPropertyColumnConfig} from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {SortDescriptor}                from '@progress/kendo-data-query';
import {ApDynGridPagerConfigBase}      from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {ApDynGridEditColumnConfig}     from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApJobsEditComponent
}                                      from '../../../entry-components/ap-administration-entry-components/ap-jobs-edit.component';
import {JobsStore}                     from '../../../stores/administration/jobs.store';
import {filter, map}                   from 'rxjs/operators';
import {FormStore}                     from '../../../stores/layout/form.store';
import {MapViewStore}                  from '../../../stores/layout/mapview.store';
import {LanguageStore}                 from '../../../stores/translation/language.store';
import * as moment                     from 'moment';
import {ApDynGridPagerConfig}          from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {LoginStore}                    from '../../../stores/login/login.store';
import {IGridStatus}                   from '../../../ap-interface/interfaces/ap-grid-status.interface';
import {IJobsGridData}                 from './ap-jobs.types';
import {nameOf}                        from '../../../ap-core/utils/ap-name-of';
import {JobsService}                   from '../../../services/data/administration/jobs.service';
import {ApDateService}                 from '../../../ap-core/services/ap-date-service';
import IJobs = Data.Job.IJobs;

@Component({
  selector: 'ap-jobs',
  template: `
    <ap-dyncomponent [caption]="'Global__Overview_Jobs'"
                     [headerIcon]="'ap-basedata-icon'"
                     [canSearch]="true"
                     [canCreate]="false"
                     [columns]="columns$ | async"
                     [items]="items$ | async"
                     [loading$]="loading$"
                     [pager]="pager"
                     (edit)="showDetails($event)"
    ></ap-dyncomponent>
  `
})
export class ApJobsComponent implements OnInit {
  public loading$: Observable<boolean> = of(false);
  public columns$: Observable<ApDynGridColumnConfigBase[]> = of([]);
  public items$: Observable<IJobsGridData[]> = of([]);
  public pager: ApDynGridPagerConfigBase;
  public sort: SortDescriptor[];

  constructor(private jobsStore: JobsStore,
              private formStore: FormStore,
              private loginStore: LoginStore,
              private mapViewStore: MapViewStore,
              private languageStore: LanguageStore,
              private jobService: JobsService,
              private dateService: ApDateService) {
  }

  public ngOnInit(): void {
    this.mapViewStore.hideMapView();
    this.loading$ = this.jobsStore.Listen(s => s.farmJobsLoading);
    this.jobsStore.loadAllFarmJobs(this.loginStore.SelectedFarmId, 90);
    this.pager = new ApDynGridPagerConfig({});
    this.setItems();
    this.buildGrid();
  }

  /**
   * Handler for showing job details
   * User wants to see details
   */
  public showDetails(dataItem: IJobsGridData): void {
    this.formStore.openForm({component: ApJobsEditComponent, data: dataItem.SourceItem});
  }

  private buildGrid(): void {
    this.columns$ = this.languageStore.SelectedLanguage$.pipe(
      map(language => {
        return [
          new ApDynGridStatusColumnConfig({
            field: `${nameOf<IJobsGridData>('Status')}.${nameOf<IGridStatus>('state')}`,
            class: `${nameOf<IJobsGridData>('Status')}.${nameOf<IGridStatus>('class')}`,
            description: `${nameOf<IJobsGridData>('Status')}.${nameOf<IGridStatus>('description')}`,
            showHeaderTitle: false,
            filterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IJobsGridData>('JobTypeName'),
            title: 'Global_Type',
            headerFilterable: true,
            filterable: true,
            width: 140
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IJobsGridData>('CreatedAtDate'),
            title: 'Global__CreatedAt',
            format: language.DateTimeFormat,
            headerFilterable: true,
            width: 140,
            sortIndex: 0,
            sortDesc: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IJobsGridData>('StartedAtDate'),
            title: 'Global_Started_At',
            format: language.DateTimeFormat,
            headerFilterable: true,
            width: 140
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IJobsGridData>('Duration'),
            title: 'Docu_Ini__Duration',
            headerFilterable: true,
            width: 90
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IJobsGridData>('PriorityText'),
            title: 'Global_Priority',
            headerFilterable: true,
            filterable: true,
            width: 80
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IJobsGridData>('InputShortened'),
            title: 'Global_Input',
            tooltip: (item: IJobsGridData) => item.SourceItem.Input,
            headerFilterable: true,
            hide: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IJobsGridData>('OutputShortened'),
            title: 'Global_Output',
            tooltip: (item: IJobsGridData) => item.SourceItem.Output,
            headerFilterable: true,
            hide: true
          }),
          new ApDynGridEditColumnConfig({})
        ];
      })
    );
  }

  private setItems(): void {
    this.items$ = combineLatest(([
      this.jobsStore.Listen(s => s.allFarmJobs)
    ])).pipe(
      filter(([farmJobs]) => !!farmJobs),
      map(([farmJobs]) => farmJobs.map(job => {
        const jobGridItem: IJobsGridData = {
          Id: job.Id,
          Status: this.jobService.getStatus(job),
          JobTypeName: job.JobType.Name,
          CreatedAtDate: !!job.CreatedAt ? this.dateService.dateToMoment(new Date(job.CreatedAt)).toDate() : undefined,
          StartedAtDate: !!job.StartedAt ? this.dateService.dateToMoment(new Date(job.StartedAt)).toDate() : undefined,
          Duration: this.extractDuration(job),
          IntervalFormatted: !!job.Schedule && job.Schedule.Interval ? job.Schedule.Interval : '-',
          InputShortened: job.Input,
          OutputShortened: job.Output,
          PriorityText: !!job.Priority ? job.Priority : '',
          SourceItem: job
        };
        return jobGridItem;
      })));
  }

  private extractDuration(job: IJobs): string {
    const diff = moment(job.FinishedAt).diff(moment(job.StartedAt));
    return diff ? moment.utc(diff).format('HH:mm:ss') : '';
  }
}
